import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { Organization, Role, User } from '../types/multiTenant';
import organizationService from '../services/organizationService';
import { useAuth } from './AuthContext';

/**
 * Organization Context State
 */
interface OrganizationContextState {
  // Data
  organization: Organization | null;
  currentUser: User | null;
  roles: Role[];
  users: User[];

  // Status
  isLoading: boolean;
  error: string | null;
  isInitialized: boolean;

  // Actions
  loadOrganization: (organizationId: string) => Promise<void>;
  loadOrganizationBySlug: (slug: string) => Promise<void>;
  refreshOrganization: () => Promise<void>;
  updateOrganization: (updates: Partial<Organization>) => Promise<boolean>;

  // User actions
  setCurrentUser: (user: User | null) => void;
  loadCurrentUser: (userId: string) => Promise<void>;
  refreshCurrentUser: () => Promise<void>;

  // Permission checks
  hasPermission: (entity: string, action: 'create' | 'read' | 'update' | 'delete') => boolean;
  canManageTeam: () => boolean;
  canManageSettings: () => boolean;
  isAdmin: () => boolean;

  // Helpers
  getTerminology: (key: 'client' | 'session' | 'appointment') => string;
  isFeatureEnabled: (feature: string) => boolean;
}

const OrganizationContext = createContext<OrganizationContextState | undefined>(undefined);

/**
 * Organization Provider Props
 */
interface OrganizationProviderProps {
  children: React.ReactNode;
  organizationId?: string; // Optional: can be loaded later
}

/**
 * Organization Provider Component
 */
export const OrganizationProvider: React.FC<OrganizationProviderProps> = ({
  children,
  organizationId,
}) => {
  // Get user from AuthContext
  const { user: authUser } = useAuth();

  const [organization, setOrganization] = useState<Organization | null>(null);
  const [currentUser, setCurrentUserState] = useState<User | null>(null);
  const [roles, setRoles] = useState<Role[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);

  /**
   * Auto-sync currentUser from AuthContext when user logs in
   * Load organization data from Supabase or use fallback when unavailable
   */
  useEffect(() => {
    if (authUser) {
      setCurrentUserState(authUser);

      // If in fallback mode, create fallback organization data
      if (authUser.organizationId === 'default-org' || authUser.roleId === 'default-role') {
        // Set fallback organization
        setOrganization({
          id: 'default-org',
          name: 'Minha Organizacao',
          slug: 'minha-organizacao',
          businessType: 'psychology',
          planId: 'small_clinic',
          subscriptionStatus: 'active',
          maxUsers: 10,
          maxClients: 200,
          config: {
            terminology: {
              client: 'Paciente',
              session: 'Sessao',
              appointment: 'Consulta',
            },
            features: {
              videoCall: false,
              aiInsights: false,
              notifications: true,
              calendar: true,
              financial: true,
            },
            notifications: {
              whatsapp: false,
              email: true,
            },
            customFields: [],
          },
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          deleted: false,
        });

        // Set fallback role with READ-ONLY permissions (security measure)
        // When Supabase is unavailable, users should not be able to modify data
        setRoles([{
          id: 'default-role',
          organizationId: 'default-org',
          name: 'viewer',
          displayName: 'Visualizador (Modo Offline)',
          permissions: {
            clients: { create: false, read: true, update: false, delete: false },
            sessions: { create: false, read: true, update: false, delete: false },
            appointments: { create: false, read: true, update: false, delete: false },
            financial: { read: true, update: false },
            team: { invite: false, manage: false },
            settings: { read: true, update: false },
          },
          isSystemRole: true,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          deleted: false,
        }]);

        // Set fallback user in users list
        setUsers([authUser]);
        setIsInitialized(true);
      } else {
        // Load real organization data from Supabase
        loadOrganization(authUser.organizationId);
      }
    } else {
      setCurrentUserState(null);
      setOrganization(null);
      setRoles([]);
      setUsers([]);
      setIsInitialized(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authUser]);

  /**
   * Load organization data by ID
   */
  const loadOrganization = useCallback(async (orgId: string) => {
    setIsLoading(true);
    setError(null);

    try {
      const org = await organizationService.getOrganization(orgId);

      if (!org) {
        setError('Organization not found');
        setIsLoading(false);
        return;
      }

      setOrganization(org);

      // Load roles and users in parallel
      const [rolesData, usersData] = await Promise.all([
        organizationService.getOrganizationRoles(orgId),
        organizationService.getOrganizationUsers(orgId),
      ]);

      setRoles(rolesData);
      setUsers(usersData);

      // Update currentUser with fresh data from database
      // This ensures role changes are reflected even if localStorage has stale data
      if (currentUser) {
        const freshUser = usersData.find(u => u.id === currentUser.id);
        if (freshUser) {
          setCurrentUserState(freshUser);
        }
      }

      setIsInitialized(true);
    } catch (err) {
      console.error('Error loading organization:', err);
      setError('Failed to load organization');
    } finally {
      setIsLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.id]);

  /**
   * Load organization data by slug
   */
  const loadOrganizationBySlug = useCallback(async (slug: string) => {
    setIsLoading(true);
    setError(null);

    try {
      const org = await organizationService.getOrganizationBySlug(slug);

      if (!org) {
        setError('Organization not found');
        setIsLoading(false);
        return;
      }

      await loadOrganization(org.id);
    } catch (err) {
      console.error('Error loading organization by slug:', err);
      setError('Failed to load organization');
      setIsLoading(false);
    }
  }, [loadOrganization]);

  /**
   * Refresh current organization data
   */
  const refreshOrganization = useCallback(async () => {
    if (!organization) return;
    await loadOrganization(organization.id);
  }, [organization, loadOrganization]);

  /**
   * Update organization
   */
  const updateOrganization = useCallback(async (updates: Partial<Organization>): Promise<boolean> => {
    if (!organization) return false;

    setIsLoading(true);
    setError(null);

    try {
      const updated = await organizationService.updateOrganization(organization.id, updates);

      if (updated) {
        setOrganization(updated);
        return true;
      }

      setError('Failed to update organization');
      return false;
    } catch (err) {
      console.error('Error updating organization:', err);
      setError('Failed to update organization');
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [organization]);

  /**
   * Set current user
   */
  const setCurrentUser = useCallback((user: User | null) => {
    setCurrentUserState(user);
  }, []);

  /**
   * Load current user by ID
   */
  const loadCurrentUser = useCallback(async (userId: string) => {
    try {
      const user = await organizationService.getUserById(userId);
      setCurrentUserState(user);
    } catch (err) {
      console.error('Error loading current user:', err);
    }
  }, []);

  /**
   * Refresh current user data
   */
  const refreshCurrentUser = useCallback(async () => {
    if (!currentUser) return;
    await loadCurrentUser(currentUser.id);
  }, [currentUser, loadCurrentUser]);

  /**
   * Check if we're in fallback mode (Supabase unavailable)
   * In fallback mode, grant full permissions to allow app to work
   */
  const isFallbackMode = useCallback((): boolean => {
    return currentUser?.organizationId === 'default-org' || currentUser?.roleId === 'default-role';
  }, [currentUser]);

  /**
   * Check if current user has permission
   */
  const hasPermission = useCallback(
    (entity: string, action: 'create' | 'read' | 'update' | 'delete'): boolean => {
      // In fallback mode, use the restricted fallback role permissions
      // This ensures users can only READ data when Supabase is unavailable

      if (!currentUser?.roleId) return false;
      const userRole = roles.find(r => r.id === currentUser.roleId);
      if (!userRole) return false;
      return organizationService.hasPermission(userRole, entity, action);
    },
    [currentUser, roles]
  );

  /**
   * Check if user can manage team
   */
  const canManageTeam = useCallback((): boolean => {
    // In fallback mode, team management is disabled for security

    if (!currentUser?.roleId) return false;
    const userRole = roles.find(r => r.id === currentUser.roleId);
    if (!userRole?.permissions?.team) return false;
    return userRole.permissions.team.manage === true;
  }, [currentUser, roles]);

  /**
   * Check if user can manage settings
   */
  const canManageSettings = useCallback((): boolean => {
    // In fallback mode, settings management is disabled for security

    if (!currentUser?.roleId) return false;
    const userRole = roles.find(r => r.id === currentUser.roleId);
    if (!userRole?.permissions?.settings) return false;
    return userRole.permissions.settings.update === true;
  }, [currentUser, roles]);

  /**
   * Check if user is admin
   */
  const isAdmin = useCallback((): boolean => {
    // In fallback mode, user is NOT admin (security measure)

    if (!currentUser?.roleId) return false;
    const userRole = roles.find(r => r.id === currentUser.roleId);
    return userRole?.name === 'admin';
  }, [currentUser, roles]);

  /**
   * Get terminology for entity type
   */
  const getTerminology = useCallback(
    (key: 'client' | 'session' | 'appointment'): string => {
      if (!organization?.config?.terminology) {
        // Defaults
        const defaults = {
          client: 'Cliente',
          session: 'Sessão',
          appointment: 'Consulta',
        };
        return defaults[key];
      }

      return organization.config.terminology[key] || key;
    },
    [organization]
  );

  /**
   * Check if feature is enabled
   */
  const isFeatureEnabled = useCallback(
    (feature: string): boolean => {
      if (!organization?.config?.features) return false;
      return (organization.config.features as any)[feature] === true;
    },
    [organization]
  );

  /**
   * Auto-load organization on mount if organizationId provided
   */
  useEffect(() => {
    if (organizationId && !isInitialized) {
      loadOrganization(organizationId);
    }
  }, [organizationId, isInitialized, loadOrganization]);

  // Memoizar o value do Context: sem isso, todo re-render do Provider cria
  // novo objeto value → consumidores (useClients/useSessions/useAppointments)
  // veem "context mudou" e disparam fetch novamente. Era a causa raiz da
  // triplicação de queries Supabase por navegação (org/roles/users/clients/
  // sessions/appointments × 3 cada). Dependências listadas explicitamente.
  const value: OrganizationContextState = useMemo(() => ({
    // Data
    organization,
    currentUser,
    roles,
    users,

    // Status
    isLoading,
    error,
    isInitialized,

    // Actions
    loadOrganization,
    loadOrganizationBySlug,
    refreshOrganization,
    updateOrganization,

    // User actions
    setCurrentUser,
    loadCurrentUser,
    refreshCurrentUser,

    // Permission checks
    hasPermission,
    canManageTeam,
    canManageSettings,
    isAdmin,

    // Helpers
    getTerminology,
    isFeatureEnabled,
  }), [
    organization,
    currentUser,
    roles,
    users,
    isLoading,
    error,
    isInitialized,
    loadOrganization,
    loadOrganizationBySlug,
    refreshOrganization,
    updateOrganization,
    setCurrentUser,
    loadCurrentUser,
    refreshCurrentUser,
    hasPermission,
    canManageTeam,
    canManageSettings,
    isAdmin,
    getTerminology,
    isFeatureEnabled,
  ]);

  return (
    <OrganizationContext.Provider value={value}>
      {children}
    </OrganizationContext.Provider>
  );
};

/**
 * Hook to use Organization Context
 */
export const useOrganization = (): OrganizationContextState => {
  const context = useContext(OrganizationContext);

  if (context === undefined) {
    throw new Error('useOrganization must be used within OrganizationProvider');
  }

  return context;
};

/**
 * Hook for permission checks only
 */
export const usePermissions = () => {
  const { hasPermission, canManageTeam, canManageSettings, isAdmin } = useOrganization();

  return {
    hasPermission,
    canManageTeam,
    canManageSettings,
    isAdmin,
  };
};

/**
 * Hook for organization data only (read-only)
 */
export const useOrganizationData = () => {
  const { organization, currentUser, roles, users, isLoading, error, isInitialized } = useOrganization();

  return {
    organization,
    currentUser,
    roles,
    users,
    isLoading,
    error,
    isInitialized,
  };
};

export default OrganizationContext;
